// ── users-collection: useCollection server adapter for /users ─────────────

import { useMemo } from "react";
import {
  Badge,
  Button,
  humanizeLabel,
  StatusDot,
  useCollection,
  type CollectionColumnDef,
  type CollectionFetcherInput,
  type CollectionFetcherResult,
  type CollectionFilterDef,
  type UseCollectionResult
} from "@vjal/ui";

import { destructiveButtonClass } from "../../components/ui-classes";
import type { CoachingUserDto } from "../../services/coaching-dtos";
import { readDirectoryInitialState } from "../directory-collection-state";
import { ASSIGNABLE_ROLES, USER_FILTER_STATUSES } from "./users-write";

export const USERS_PAGE_SIZE = 25;
export const USERS_DEFAULT_SORT = { id: "name", dir: "asc" as const };

export type UserRow = CoachingUserDto & Record<string, unknown>;
function Dash() {
  return <span className="text-ink-muted">—</span>;
}

function statusTone(status: string | undefined): "ok" | "warn" | "idle" {
  if (status === "active") return "ok";
  if (status === "suspended") return "warn";
  return "idle";
}

export interface UsersCollectionActions {
  allowUpdate: boolean;
  onEdit: (row: CoachingUserDto) => void;
  onDeactivate: (row: CoachingUserDto) => void;
}

export function buildUsersFilterDefs(): CollectionFilterDef<UserRow>[] {
  return [
    {
      id: "role",
      label: "Role",
      type: "select",
      placeholder: "All roles",
      getValue: (row) => row.role ?? "",
      options: ASSIGNABLE_ROLES.map((role) => ({
        value: role,
        label: humanizeLabel(role)
      }))
    },
    {
      id: "status",
      label: "Status",
      type: "select",
      placeholder: "All statuses",
      getValue: (row) => row.membershipStatus ?? row.status ?? "",
      options: USER_FILTER_STATUSES.map((status) => ({
        value: status,
        label: humanizeLabel(status)
      }))
    }
  ];
}

export function buildUsersCollectionColumns(actions: UsersCollectionActions): CollectionColumnDef<UserRow>[] {
  const columns: CollectionColumnDef<UserRow>[] = [
    {
      id: "name",
      header: "User",
      sortable: true,
      sortAccessor: (row) => row.name ?? row.email ?? "",
      searchValue: (row) => `${row.name ?? ""} ${row.email ?? ""}`,
      priority: 1,
      cell: (row) => (
        <span className="font-medium text-strong">{row.name || row.email || `User ${row.id}`}</span>
      )
    },
    {
      id: "email",
      header: "Email",
      sortable: true,
      sortAccessor: (row) => row.email ?? "",
      priority: 2,
      cell: (row) => (row.email ? <span className="text-ink-body">{row.email}</span> : <Dash  />)
    },
    {
      id: "role",
      header: "Role",
      sortable: true,
      sortAccessor: (row) => row.role ?? "",
      priority: 2,
      cell: (row) => (row.role ? <Badge>{humanizeLabel(row.role)}</Badge> : <Dash  />)
    },
    {
      id: "status",
      header: "Status",
      sortable: true,
      sortAccessor: (row) => row.membershipStatus ?? row.status ?? "",
      priority: 2,
      cell: (row) => (
        <span className="inline-flex items-center gap-2">
          <StatusDot tone={statusTone(row.membershipStatus ?? row.status)}  />
          <span className="text-ink-body">{humanizeLabel(row.membershipStatus ?? row.status ?? "unknown")}</span>
        </span>
      )
    },
    {
      id: "scope_label",
      header: "Scope",
      sortable: false,
      priority: 3,
      cell: (row) => (row.scope_label ? <span className="text-ink-body">{row.scope_label}</span> : <Dash  />)
    }
  ];

  if (actions.allowUpdate) {
    columns.push({
      id: "_actions",
      header: "",
      sortable: false,
      priority: 1,
      cell: (row) => (
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => actions.onEdit(row)}
            aria-label={`Edit ${row.name || row.email}`}
            data-exact-set-name="Edit user">
            Edit
          </Button>
          <Button
            variant="danger"
            size="sm"
            className={destructiveButtonClass}
            onClick={() => actions.onDeactivate(row)}
            aria-label={`Deactivate ${row.name || row.email}`}
            disabled={row.membershipStatus === "inactive" || row.membershipStatus === "removed" || (!row.membershipStatus && row.status === "inactive")}
            data-exact-set-name="Deactivate user">
            Deactivate
          </Button>
        </div>
      )
    });
  }

  return columns;
}

export type UsersCollectionFetcher = (
  input: CollectionFetcherInput
) => Promise<CollectionFetcherResult<UserRow>>;

export function useUsersCollection(
  fetcher: UsersCollectionFetcher,
  actions: UsersCollectionActions,
  routePath?: string
): UseCollectionResult<UserRow> {
  const filters = useMemo(() => buildUsersFilterDefs(), []);
  const columns = useMemo(() => buildUsersCollectionColumns(actions), [actions]);
  const initialState = useMemo(
    () => ({ sort: USERS_DEFAULT_SORT, ...readDirectoryInitialState(routePath, USERS_PAGE_SIZE) }),
    [routePath]
  );

  return useCollection<UserRow>({
    mode: "server",
    fetcher,
    columns,
    filters,
    pageSize: USERS_PAGE_SIZE,
    keepRowsWhileRefreshing: true,
    urlSync: "query",
    views: "coaching-users",
    initialState,
    getRowKey: (row) => row.id
  });
}
